/**
 * ISO-13399 tool field registry.
 *
 * The backend transmits tool geometry under the French ISO-13399 column aliases
 * (e.g. "Nb dents", "hélice 1"). This module maps each alias to a translation
 * key under `toolRecommender.fields.*` and defines the canonical display order.
 * Unknown aliases have no key and are displayed raw as a fallback.
 */

const FIELD_SLUGS: Record<string, string> = {
  d1: 'd1',
  l1: 'l1',
  'Nb dents': 'nbDents',
  'hélice 1': 'helice1',
  'hélice 2': 'helice2',
  'Sens de coupe': 'sensDeCoupe',
  Profil: 'profil',
  'Pas du profil': 'pasDuProfil',
  'Forme de la fraise': 'formeFraise',
  Coin: 'coin',
  'Valeur coin (Exemple : 0 =rien / 0.05x45° / R0.1)': 'valeurCoin',
  'Coupe au centre': 'coupeCentre',
  'Cassé de coupe': 'casseCoupe',
  'Largeur cassé de coupe': 'largeurCasseCoupe',
  'Angle de coupe bout': 'angleCoupeBout',
  'Angle de coupe diamètre mini goujure 1': 'angleCoupeDiametreMin',
  'Angle de coupe diamètre maxi goujure 1': 'angleCoupeDiametreMax',
  '1ère dépouille bout': 'depouilleBout1',
  '2ème dépouille bout': 'depouilleBout2',
  '1ère dépouille diamètre': 'depouilleDiam1',
  '2ème dépouille diamètre': 'depouilleDiam2',
  'type de dépouille diamètre': 'depouilleDiamType',
  'Affûtage bout': 'affutageBout',
  'Largeur affûtage bout': 'largeurAffutageBout',
  'Dépassement du centre': 'depassementCentre',
  'Largeur listel': 'largeurListel',
  "Largeur d'âme": 'largeurAme',
  'Noyau [mm]': 'noyauMm',
  'Noyau arrière': 'noyauArriere',
  'Creux 1 [°]': 'creux1',
  'Creux 2 [°]': 'creux2',
  'Creux 3 [°]': 'creux3',
  'longueur creux': 'longueurCreux',
  'Indexation taillage': 'indexTaillage',
  'Indexation taillage angle': 'indexTaillageAngle',
  'Rayon de rectifiage': 'rayonRectifiage',
  'Longueur utile maximum LUX [mm]': 'longueurUtileMax',
  'Demi angle du cône du corps BHTA [°]': 'demiAngleCone',
  // The ISO schema alias contains a double space; user-supplied tools often
  // carry the single-space spelling — both map to the same label.
  'diam. collet  #1 (D2)': 'diamCollet',
  'diam. collet #1 (D2)': 'diamCollet',
  'long. collet #1 (L2)': 'longCollet',
  'Type de fabrication': 'fabricationType',
  'Substrat / matière outil': 'substrat',
  '% de Co': 'percentCo',
  'Taille de grain': 'tailleGrain',
  Référence: 'reference',
  article: 'article',
};

/** Canonical display order: primary dimensions, cutting geometry, grinding, material, refs. */
export const TOOL_FIELD_ORDER: string[] = [
  'd1',
  'l1',
  'Nb dents',
  'hélice 1',
  'hélice 2',
  'Sens de coupe',
  'Profil',
  'Pas du profil',
  'Forme de la fraise',
  'Coin',
  'Valeur coin (Exemple : 0 =rien / 0.05x45° / R0.1)',
  'Coupe au centre',
  'Cassé de coupe',
  'Largeur cassé de coupe',
  'Angle de coupe bout',
  'Angle de coupe diamètre mini goujure 1',
  'Angle de coupe diamètre maxi goujure 1',
  '1ère dépouille bout',
  '2ème dépouille bout',
  '1ère dépouille diamètre',
  '2ème dépouille diamètre',
  'type de dépouille diamètre',
  'Affûtage bout',
  'Largeur affûtage bout',
  'Dépassement du centre',
  'Largeur listel',
  "Largeur d'âme",
  'Noyau [mm]',
  'Noyau arrière',
  'Creux 1 [°]',
  'Creux 2 [°]',
  'Creux 3 [°]',
  'longueur creux',
  'Indexation taillage',
  'Indexation taillage angle',
  'Rayon de rectifiage',
  'Longueur utile maximum LUX [mm]',
  'Demi angle du cône du corps BHTA [°]',
  'diam. collet  #1 (D2)',
  'diam. collet #1 (D2)',
  'long. collet #1 (L2)',
  'Type de fabrication',
  'Substrat / matière outil',
  '% de Co',
  'Taille de grain',
  'Référence',
  'article',
];

export function toolFieldLabelKey(alias: string): string | null {
  const slug = FIELD_SLUGS[alias];
  return slug ? `toolRecommender.fields.${slug}` : null;
}
